/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-no-bind */
import {
    AutocompleteArrayInput,
    AutocompleteInput,
    BooleanInput,
    NumberInput,
    ReferenceArrayInput,
    ReferenceInput,
    required,
    TabbedForm,
    TextInput,
    useGetOne,
    useTranslate
} from "react-admin";

import { useInputStyles } from "../../../utils/useInputStyles";

import { ImageListInput } from "../../image/components/Form";
import { CreateToolbar } from "../../../common/CreateToolbar";
import { EditToolbar } from "../../../common/EditToolbar";
import { PublicationFormGroup } from "../../../utils/components/form/PublicationFormGroup";
import { ProductRecord } from "../ProductRecord";

type FormMode = "duplicate" | "create" | "edit";

export function ProductSimpleFormContent(): JSX.Element {
    const classes = useInputStyles;
    const t = useTranslate();

    // Rebuild a fake url with hash
    const url = new URL("http://localhost" + window.location.hash?.slice(1));
    let formMode: FormMode = "edit";
    const duplicateId = url.searchParams?.get("duplicate");

    if (url.pathname.includes("/create")) {
        formMode = duplicateId != null ? "duplicate" : "create";
    }

    const { data: duplicateObj } = useGetOne<ProductRecord>("product", { id: duplicateId ?? "" });
    const formProps: any = {};

    if (formMode === "edit") {
        // Edit
        formProps["toolbar"] = <EditToolbar />;
    } else {
        // Create or duplicate
        if (formMode === "duplicate") {
            formProps["defaultValues"] = {
                formatId: duplicateObj?.formatId,
                contentOriginId: duplicateObj?.contentOriginId,
                licenseId: duplicateObj?.licenseId,
                rightHolderId: duplicateObj?.rightHolderId,
                characterId: duplicateObj?.characterId,
                contentTypeId: duplicateObj?.contentTypeId,
                seasonId: duplicateObj?.seasonId,
                collection: duplicateObj?.collection,
                keywords: duplicateObj?.keywords,
                serieKeywords: duplicateObj?.serieKeywords,
                genreIds: duplicateObj?.genreIds ?? [],
                themeIds: duplicateObj?.themeIds ?? [],
                minAge: duplicateObj?.minAge,
                maxAge: duplicateObj?.maxAge,
                status: duplicateObj?.status ?? "draft",
                country: duplicateObj?.country,
                year: duplicateObj?.year,
                duration: duplicateObj?.duration,
                version: duplicateObj?.version,
                actors: duplicateObj?.actors,
                authors: duplicateObj?.authors,
                illustrators: duplicateObj?.illustrators,
                directors: duplicateObj?.directors,
                producers: duplicateObj?.producers,
                credits: duplicateObj?.credits,
                soundContributors: duplicateObj?.soundContributors,
                voiceContributors: duplicateObj?.voiceContributors,
                publicationsStartedAt: duplicateObj?.publicationStartedAt,
                publicationEndedAt: duplicateObj?.publicationStartedAt
            };
        }

        formProps["toolbar"] = <CreateToolbar />;
    }

    return (
        <>
            <TabbedForm {...formProps}>
                <TabbedForm.Tab label="general">
                    <TextInput sx={classes.inputField} source="name" validate={required()} />
                    <TextInput sx={classes.inputField} source="codeEm" />
                    <TextInput sx={classes.inputField} source="codeEmArte" />
                    <TextInput sx={classes.inputField} source="codeBnf" />
                    <ReferenceInput source="formatId" reference="format">
                        <AutocompleteInput optionValue="id" optionText="name" fullWidth />
                    </ReferenceInput>
                    <ReferenceInput source="contentOriginId" reference="contentOrigin">
                        <AutocompleteInput
                            label={t("resources.product.fields.contentOrigin")}
                            optionValue="id"
                            optionText="name"
                            fullWidth
                        />
                    </ReferenceInput>
                    <ReferenceInput source="licenseId" reference="license">
                        <AutocompleteInput label={t("resources.product.fields.license")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceInput>
                    <ReferenceInput source="rightHolderId" reference="rightHolder">
                        <AutocompleteInput label={t("resources.product.fields.rightHolder")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceInput>
                    <ReferenceInput source="characterId" reference="character">
                        <AutocompleteInput label={t("resources.product.fields.character")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceInput>
                    <ReferenceInput source="contentTypeId" reference="contentType">
                        <AutocompleteInput label={t("resources.product.fields.contentType")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceInput>
                    <ReferenceInput source="seasonId" reference="season">
                        <AutocompleteInput
                            label={t("resources.product.fields.season")}
                            optionValue="id"
                            optionText={(season) => `${season.name} - ${season.serie.name}`}
                            fullWidth
                        />
                    </ReferenceInput>
                    <NumberInput sx={classes.inputField} source="episodePosition" min={1} />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="editorial">
                    <TextInput sx={classes.inputField} source="title" />
                    <TextInput sx={classes.inputField} source="description" fullWidth multiline />
                    <TextInput sx={classes.inputField} source="collection" />
                    <TextInput sx={classes.inputField} source="keywords" />
                    <TextInput sx={classes.inputField} source="serieKeywords" />
                    <ReferenceArrayInput source="genreIds" reference="genre">
                        <AutocompleteArrayInput label={t("resources.product.fields.genres")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceArrayInput>
                    <ReferenceArrayInput source="themeIds" reference="theme">
                        <AutocompleteArrayInput label={t("resources.product.fields.themes")} optionValue="id" optionText="name" fullWidth />
                    </ReferenceArrayInput>
                </TabbedForm.Tab>
                <TabbedForm.Tab label={t("resources.product.tabs.technicalInfo")}>
                    <TextInput sx={classes.inputField} source="country" />
                    <NumberInput sx={classes.inputField} source="year" min={1000} />
                    <NumberInput sx={classes.inputField} source="duration" min={0} />
                    <TextInput sx={classes.inputField} source="version" />
                    <BooleanInput sx={classes.inputField} source="retranscription" />
                    <TextInput sx={classes.inputField} source="credits" fullWidth multiline />
                    <TextInput sx={classes.inputField} source="authors" />
                    <TextInput sx={classes.inputField} source="directors" />
                    <TextInput sx={classes.inputField} source="actors" />
                    <TextInput sx={classes.inputField} source="producers" />
                    <TextInput sx={classes.inputField} source="composers" />
                    <TextInput sx={classes.inputField} source="illustrators" />
                    <TextInput sx={classes.inputField} source="soundContributors" />
                    <TextInput sx={classes.inputField} source="voiceContributors" />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="publication">
                    <PublicationFormGroup />
                    <NumberInput sx={classes.inputField} source="minAge" min={0} />
                    <NumberInput sx={classes.inputField} source="maxAge" min={0} />
                </TabbedForm.Tab>
                <TabbedForm.Tab label="media" path="media">
                    <ImageListInput targetType="product" />
                </TabbedForm.Tab>
            </TabbedForm>
        </>
    );
}
